import React, { createContext, useContext, useState, useCallback } from "react";
import { getDataRegionForCountryCode, normalizeCountryCode } from "./countries";

const API_URLS = {
  de: process.env.REACT_APP_API_URL_DE || process.env.REACT_APP_API_URL || "",
  ru: process.env.REACT_APP_API_URL_RU || "https://api-ru.swingmentor.pro",
};

const normalizeApiBase = (url) => String(url || "").trim().replace(/\/+$/, "");

const normalizeDataRegion = (region) => {
  const value = String(region || "").trim().toLowerCase();
  return value === "ru" ? "ru" : "de";
};

const apiForCountry = (countryCode) => {
  const region = getDataRegionForCountryCode(countryCode);
  return API_URLS[region] || API_URLS.de;
};

const apiForDataRegion = (region) => (
  API_URLS[normalizeDataRegion(region)] || API_URLS.de
);

const uniqueApiBases = (bases) => (
  bases
    .map(normalizeApiBase)
    .filter((base, index, allBases) => allBases.indexOf(base) === index)
);

const postJson = async (apiBase, path, body) => {
  const res = await fetch(`${apiBase}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
};

const postRegionalJson = async (countryCode, path, body) => {
  let result = await postJson(apiForCountry(countryCode), path, body);
  if (!result.res.ok && result.data?.code === "wrong_data_region" && result.data?.api_base_url) {
    result = await postJson(result.data.api_base_url.replace(/\/+$/, ""), path, body);
  }
  return result;
};

const postLoginJson = async (path, body, preferredRegion) => {
  const candidateBases = uniqueApiBases([
    preferredRegion ? apiForDataRegion(preferredRegion) : null,
    API_URLS.de,
    API_URLS.ru,
  ]);
  let lastResult = null;

  for (const apiBase of candidateBases) {
    let result;

    try {
      result = await postJson(apiBase, path, body);
    } catch {
      lastResult = { res: { ok: false }, data: { error: "Login failed" } };
      continue;
    }

    if (!result.res.ok && result.data?.code === "wrong_data_region" && result.data?.api_base_url) {
      try {
        result = await postJson(result.data.api_base_url.replace(/\/+$/, ""), path, body);
      } catch {
        lastResult = { res: { ok: false }, data: { error: "Login failed" } };
        continue;
      }
    }

    if (result.res.ok || result.data?.code === "email_not_verified") {
      return result;
    }

    lastResult = result;
  }

  return lastResult || postJson(API_URLS.de, path, body);
};

const normalizeLoginPayload = (data, fallbackCountryCode) => {
  const responseData = data && typeof data === "object" ? data : {};
  const sourceUser = responseData.user && typeof responseData.user === "object"
    ? responseData.user
    : responseData;
  const userData = { ...sourceUser };

  if (!userData.id && userData.user_id) userData.id = userData.user_id;
  if (!userData.user_id && userData.id) userData.user_id = userData.id;
  if (!userData.country_code) userData.country_code = responseData.country_code || fallbackCountryCode;
  if (!userData.data_region) userData.data_region = responseData.data_region;
  if (!userData.name) {
    userData.name = `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
  }

  return {
    userData,
    token: responseData.token || responseData.auth_token || responseData.access_token || null,
  };
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("sm_token") || null);

  const [user, setUser] = useState(() => {
    try {
      if (!localStorage.getItem("sm_token")) {
        localStorage.removeItem("sm_user");
        return null;
      }
      const stored = localStorage.getItem("sm_user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  /* ── register ── */
  const register = useCallback(async ({ first_name, last_name, email, password, hcp, country_code, terms_accepted, privacy_accepted }) => {
    const normalizedCountryCode = normalizeCountryCode(country_code);
    const body = {
      first_name,
      last_name,
      email: String(email || "").trim().toLowerCase(),
      password,
      hcp,
      country_code: normalizedCountryCode,
      terms_accepted,
      privacy_accepted,
    };
    const { res, data } = await postRegionalJson(normalizedCountryCode, "/api/auth/register", body);
    if (!res.ok) throw new Error(data.error || "Registration failed");
    localStorage.setItem("sm_country_code", data.country_code || normalizedCountryCode);
    return data;
  }, []);

  /* ── login ── */
  const login = useCallback(async ({ email, password }) => {
    let preferredRegion = null;
    try {
      const storedUser = JSON.parse(localStorage.getItem("sm_user") || "null");
      preferredRegion = storedUser?.data_region || null;
    } catch {
      preferredRegion = null;
    }

    if (!preferredRegion) {
      preferredRegion = getDataRegionForCountryCode(localStorage.getItem("sm_country_code"));
    }

    const { res, data } = await postLoginJson("/api/auth/login", {
      email: String(email || "").trim().toLowerCase(),
      password,
    }, preferredRegion);
    if (!res.ok) throw new Error(data.error || "Login failed");

    const { userData, token: tok } = normalizeLoginPayload(data);
    setUser(userData);
    setToken(tok || null);
    localStorage.setItem("sm_user", JSON.stringify(userData));
    if (userData.country_code) localStorage.setItem("sm_country_code", userData.country_code);
    if (tok) localStorage.setItem("sm_token", tok);
    return userData;
  }, []);

  /* ── logout ── */
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("sm_user");
    localStorage.removeItem("sm_token");
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
